import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import SmartToyIcon from '@mui/icons-material/SmartToy';

const AttendanceCardView = ({ dataKey, data, t, attendanceCauseOptions = [] , userNames = []}) => {
  const [page, setPage] = useState(0);
  const itemsPerPage = 5;

  // Reset pagina quando cambiano i dati (es: cambio mese)
  useEffect(() => {
    setPage(0);
  }, [dataKey]);

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="text.secondary">
          {t("Nessun dato disponibile") || "Nessun dato disponibile"}
        </Typography>
      </Box>
    );
  }

  // Ordina i dati per data discendente (più recenti per primi)
  const sortedData = [...data].sort(
    (a, b) => new Date(b.fromDt) - new Date(a.fromDt)
  );

  const paginatedData = sortedData.slice(
    page * itemsPerPage,
    page * itemsPerPage + itemsPerPage
  );
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Formatta data e ora come Syncfusion: dd/MM/yyyy HH:mm:ss
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // Formatta solo la data con giorno della settimana
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    
    const dayNames = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
    const dayName = dayNames[date.getDay()];
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    
    return `${dayName} ${day}/${month}/${year}`;
  };

  // Estrae solo l'ora: HH:mm:ss
  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    if(isNaN(hours) || isNaN(minutes) || isNaN(seconds))
      return "Mancante";
    return `${hours}:${minutes}:${seconds}`;
  };

  // Calcola durata in hh:mm:ss
  const calculateDuration = (fromDt, toDt) => {
    if (!fromDt || !toDt) return "-";
    
    const from = new Date(fromDt);
    const to = new Date(toDt);
    const diffMs = to - from;
    if (isNaN(diffMs)) {
      return "-";}
    if (diffMs < 0) return "-";
    
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Mappa anaAttendanceTypeId al nome tramite attendanceCauseOptions (come fa Syncfusion)
  const getAttendanceTypeName = (typeId) => {
    if (!typeId || !attendanceCauseOptions || attendanceCauseOptions.length === 0) return "-";
    const option = attendanceCauseOptions.find(opt => opt.id === typeId);
    return option ? option.name : "-";
  };

  const getUserName = (userId) => {
    if (!userId || !userNames) return t("Utente Sconosciuto");
    const user = userNames.find(u => u.id === userId);
    return user ? user.name : t("Utente Sconosciuto");
  };

  return (
    <Box sx={{ 
          p: { xs: 0, sm: 2 },    // 0px mobile, 16px tablet+
          pt: { xs: 1, sm: 2 }     // 8px top mobile, 16px tablet+
        }}>
      {/* CARD LIST */}
      {paginatedData.map((item, idx) => {
        
        return (
          <Paper
            key={item.id || idx}
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2.5 },      // 12px mobile, 20px tablet+
              mb: { xs: 1.5, sm: 2 },       // 12px mobile, 16px tablet+
              mx: { xs: 0.5, sm: 0 },       // 4px laterale mobile, 0 tablet+
              position: 'relative', // <--- FONDAMENTALE: mantiene il badge dentro la card
              borderRadius: { xs: "8px", sm: "10px" },
              backgroundColor: "#ffffff", // Bianco puro per contrasto massimo
              border: "1px solid #eaeaea", // Bordo ancora più sottile e chiaro
              borderLeft: `6px solid ${
                item.autoClosed === 1 
                  ? "#0288d1" // Blu per Auto-Closed
                  : (formatTime(item.toDt) === "Mancante" ? "#ef5350" : "#66bb6a")
              }`,
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)", // Ombra quasi invisibile per dare profondità
            }}
          >
            {/* BADGE SISTEMA: Visibile solo se AutoClosed è 1 */}
            {item.autoClosed === 1 && (
              <Box sx={{ 
                  position: 'absolute', 
                  top: "-14px",          // Più spinto verso l'alto per liberare la data
                  right: "0px",         // Staccato dal bordo destro per estetica
                  bgcolor: '#0288d1', 
                  color: 'white', 
                  px: 1.5, 
                  py: 0.5, 
                  borderRadius: '20px',  // Forma a pillola molto più moderna
                  fontSize: '0.55rem', 
                  fontWeight: 800,
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 0.5,
                  boxShadow: '0px 3px 6px rgba(0,0,0,0.16)', // Ombra per dare profondità
                  zIndex: 10,
                  border: '2px solid #ffffff' // Un bordo bianco lo "stacca" visivamente dalla card
                }}>
                <SmartToyIcon sx={{ fontSize: '0.8rem' }} /> {/* Un'iconcina robot fa capire subito "Auto" */}
                {t("system")}
              </Box>
            )}
            {/* Header: Nome Utente (molto visibile) */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a1a", lineHeight: 1.2 }}>
                {getUserName(item.anaUserId)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#9e9e9e", textTransform: 'uppercase' }}>
                {formatDate(item.fromDt)}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1, borderColor: "#f0f0f0" }} />
            {/* Grid Dati: Contrasto mirato */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="caption" display="block" sx={{ color: "#757575", fontWeight: 600, mb: 0.5 }}>
                  {t("fromDt") || "Entrata"}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#2c3e50", fontSize: "0.95rem" }}>
                  {formatTime(item.fromDt)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" display="block" sx={{ color: "#757575", fontWeight: 600, mb: 0.5 }}>
                  {t("toDt") || "Uscita"}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: "0.95rem",
                    color: formatTime(item.toDt) === "Mancante" ? "#d32f2f" : "#2c3e50" 
                  }}
                >
                  {formatTime(item.toDt)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" display="block" sx={{ color: "#757575", fontWeight: 600, mb: 0.5 }}>
                  {t("timeSpent") || "Durata"}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: "#000000" }}>
                  {calculateDuration(item.fromDt, item.toDt)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" display="block" sx={{ color: "#757575", fontWeight: 600, mb: 0.5 }}>
                  Causale
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: "#616161" }}>
                  {getAttendanceTypeName(item.anaAttendanceTypeId)}
                </Typography>
              </Box>
            </Box>

            {/* Note: Box separato neutro */}
            {item.attendanceNote && (
              <>  
              <Divider sx={{ my: 1, borderStyle: "dashed", borderColor: "#e0e0e0" }} />
              <Box sx={{ 
                mt: 2, 
                p: 1.5, 
                bgcolor: "#f9f9f9", 
                borderRadius: "6px", 
                borderLeft: "3px solid #e0e0e0" 
              }}>
                <Typography variant="caption" sx={{ color: "#616161", display: "block", mb: 0.5, fontWeight: 700 }}>
                  {t("attendanceNote") || "Note"}:
                </Typography>
                <Typography variant="body2" sx={{ color: "#424242", fontSize: "0.85rem" }}>
                  {item.attendanceNote}
                </Typography>
              </Box>
              </>
              
            )}
          </Paper>
        );
      })}

      {/* PAGINATION */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Pagina {page + 1} di {totalPages}
          </Typography>
        </Box>
      )}

      {/* PAGINATION BUTTONS */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
          <Button
           variant="outlined"  // ✅ Non più "contained"
           color="primary"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            sx={{ fontWeight: 600 }}
          >
            {t("Precedente") || "Precedente"}
          </Button>
          <Button
            variant="outlined"      // ✅ Ancora meno invasivo
            color="primary"
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page === totalPages - 1}
            
          >
            {t("Successivo") || "Successivo"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AttendanceCardView;